import { IGroups } from '@llws/platform-shared-types/types/db/auth';
import {
  Not, Equal, IOrderCommand, ISearchCommandOptions, In,
  IQueryCommandOptions,
} from '@llws/dynamic-router-utils';
import { IDataTableEmitPayload, IDataTableRouteQuery, mergeQueryCommandOptionsV2 } from '@/shared';
import { IAction, IStateStrict } from '@/shared/interfaces/store-base';
import { StoreBaseImplementationV2 } from '@/shared/classes/store-base-implementation-v2';

export type TAccessLabel = 'Full Access' | 'Some Access' | 'No Access';

export interface IGroupsWithAggs extends Partial<IGroups> {
  accountModuleCount: number;
  accountPropertyModuleCount: number;
  accountSiteModuleCount: number;
  groupPropertyHasPropertyMode: number;
  groupAccountModulePartialCount: number;
  groupAccountModuleCount: number;
  groupAllPropertyModulePartialCount: number;
  groupPerPropertyModulePartialCount: number;
  groupAllPropertyModuleCount: number;
  groupPerPropertyModuleCount: number;
  groupSiteModuleUpdateCount: number;
  groupSiteModuleCount: number;
  userCount: number;
  siteAccess: TAccessLabel;
  accountAccess: TAccessLabel;
  propertyAccess: TAccessLabel
}

const defaultFilters = (clientId: Number, siteIds: number[]): ISearchCommandOptions => ({
  alias: 'g',
  aggregations: [
    {
      propertyAlias: 'groupAccountModuleCount',
      table: 'groups__has_products',
      column: 'productKey',
      aggregate: 'COUNT',
      relations: [{ path: 'product', alias: 'ap' }],
      whereCommands: [ Equal('groupId', 'g.id'), Equal('ap.modelType', 'account'), Equal('hasDelete', 1) ],
    },
    {
      propertyAlias: 'groupAccountModulePartialCount',
      table: 'groups__has_products',
      column: 'productKey',
      aggregate: 'COUNT_DISTINCT',
      relations: [{ path: 'product', alias: 'ap' }],
      whereCommands: [ Equal('groupId', 'g.id'), Equal('ap.modelType', 'account'), Equal('hasRead', 1), Not(Equal('hasDelete', 1)) ],
    },
    {
      propertyAlias: 'groupAllPropertyModuleCount',
      table: 'groups__has_products',
      column: 'productKey',
      aggregate: 'COUNT_DISTINCT',
      relations: [{ path: 'product', alias: 'ap' }],
      whereCommands: [
        Equal('groupId', 'g.id'),
        Equal('ap.modelType', 'building'),
        Equal('hasDelete', 1),
        Not(Equal('productKey', 'property_mode')),
        Equal('modelId', -1),
      ],
    },
    {
      propertyAlias: 'groupPerPropertyModuleCount',
      table: 'groups__has_products',
      column: 'productKey',
      aggregate: 'COUNT_DISTINCT',
      relations: [{ path: 'product', alias: 'ap' }],
      whereCommands: [
        Equal('groupId', 'g.id'),
        Equal('ap.modelType', 'building'),
        Equal('hasDelete', 1),
        Not(Equal('productKey', 'property_mode')),
        Not(Equal('modelId', -1)),
      ],
    },
    {
      propertyAlias: 'groupAllPropertyModulePartialCount',
      table: 'groups__has_products',
      column: 'productKey',
      aggregate: 'COUNT_DISTINCT',
      relations: [{ path: 'product', alias: 'ap' }],
      whereCommands: [
        Equal('groupId', 'g.id'),
        Equal('ap.modelType', 'building'),
        Equal('hasRead', 1),
        Not(Equal('hasDelete', 1)),
        Not(Equal('productKey', 'property_mode')),
        Equal('modelId', -1),
      ],
    },
    {
      propertyAlias: 'groupPerPropertyModulePartialCount',
      table: 'groups__has_products',
      column: 'productKey',
      aggregate: 'COUNT_DISTINCT',
      relations: [{ path: 'product', alias: 'ap' }],
      whereCommands: [
        Equal('groupId', 'g.id'),
        Equal('ap.modelType', 'building'),
        Equal('hasRead', 1),
        Not(Equal('hasDelete', 1)),
        Not(Equal('productKey', 'property_mode')),
        Not(Equal('modelId', -1)),
      ],
    },
    {
      propertyAlias: 'groupPropertyHasPropertyMode',
      table: 'groups__has_products',
      column: 'productKey',
      aggregate: 'COUNT_DISTINCT',
      relations: [{ path: 'product', alias: 'ap' }],
      whereCommands: [ Equal('groupId', 'g.id'), Equal('productKey', 'property_mode'), Equal('modelId', -2) ],
    },
    {
      propertyAlias: 'groupSiteModuleCount',
      table: 'groups__has_products',
      column: 'productKey',
      aggregate: 'COUNT',
      relations: [{ path: 'product', alias: 'ap' }],
      whereCommands: [
        Equal('groupId', 'g.id'),
        Equal('ap.modelType', 'site'),
        Equal('hasDelete', 1),
        Not(Equal('productKey', 'property_mode')),
      ],
    },
    {
      propertyAlias: 'groupSiteModuleUpdateCount',
      table: 'groups__has_products',
      column: 'productKey',
      aggregate: 'COUNT',
      relations: [{ path: 'product', alias: 'ap' }],
      whereCommands: [
        Equal('groupId', 'g.id'),
        Equal('ap.modelType', 'site'),
        Equal('hasRead', 1),
        Not(Equal('hasDelete', 1)),
        Not(Equal('productKey', 'property_mode')),
      ],
    },
    {
      propertyAlias: 'accountModuleCount',
      table: 'products__has_clients',
      column: 'productKey',
      aggregate: 'COUNT',
      relations: [{ path: 'product', alias: 'ap' }],
      whereCommands: [ Equal('clientId', `${clientId}`), Equal('ap.modelType', 'account') ],
    },
    {
      propertyAlias: 'accountPropertyModuleCount',
      table: 'products__has_clients',
      column: 'productKey',
      aggregate: 'COUNT_DISTINCT',
      relations: [{ path: 'product', alias: 'ap' }],
      whereCommands: [ Equal('clientId', `${clientId}`), Equal('ap.modelType', 'building'), Not(Equal('productKey', 'property_mode')) ],
    },
    {
      propertyAlias: 'accountSiteModuleCount',
      table: 'products__has_clients',
      column: 'productKey',
      aggregate: 'COUNT',
      relations: [{ path: 'product', alias: 'ap' }],
      whereCommands: [
        Equal('clientId', `${clientId}`),
        Equal('ap.modelType', 'site'),
        In('modelId', siteIds),
      ],
    },
    {
      propertyAlias: 'userCount',
      table: 'groups__has_users',
      column: 'userId',
      aggregate: 'COUNT',
      whereCommands: [ Equal('groupId', 'g.id') ],
    },
  ],
  select: [
    'g.id',
    'g.name',
    'g.elevation',
    'accountModuleCount',
    'accountPropertyModuleCount',
    'accountSiteModuleCount',
    'groupPropertyHasPropertyMode',
    'groupAccountModuleCount',
    'groupAccountModulePartialCount',
    'groupAllPropertyModuleCount',
    'groupPerPropertyModuleCount',
    'groupAllPropertyModulePartialCount',
    'groupPerPropertyModulePartialCount',
    'groupSiteModuleUpdateCount',
    'groupSiteModuleCount',
    'userCount',
  ],
});

const getModelAccess = (accountModuleCount: number, groupModuleCount: number, hasEditOrRead: number): TAccessLabel => {
  if (!Number(groupModuleCount) && !Number(hasEditOrRead)) {
    return 'No Access';
  }
  return (accountModuleCount === groupModuleCount && Number(hasEditOrRead) === 0) ? 'Full Access' : 'Some Access';
};

const adminGatewayResourcePath = '/perms-api/groups';

const base = new StoreBaseImplementationV2<Partial<IGroups>>({ adminGatewayResourcePath });

export default {
  namespaced: true,

  state: {
    ...base.state,
  },

  getters: {
    ...base.getters,

    listWithAggs: (state: typeof base.state) => (
      ((state.list?.data as unknown as IGroupsWithAggs[]) ?? []).map((group) => {
        const isPerPropertyMode = Number(group.groupPropertyHasPropertyMode);
        const moudleCount = isPerPropertyMode ? group.groupPerPropertyModuleCount : group.groupAllPropertyModuleCount;
        const partialModuleCount = isPerPropertyMode ? group.groupPerPropertyModulePartialCount : group.groupAllPropertyModulePartialCount;

        return {
          ...group,
          ...group.elevation ? {
            siteAccess: 'Full Access',
            accountAccess: 'Full Access',
            propertyAccess: 'Full Access',
          } : {
            siteAccess: getModelAccess(group.accountSiteModuleCount, group.groupSiteModuleCount, group.groupSiteModuleUpdateCount),
            accountAccess: getModelAccess(group.accountModuleCount, group.groupAccountModuleCount, group.groupAccountModulePartialCount),
            propertyAccess: getModelAccess(
              group.accountPropertyModuleCount,
              moudleCount,
              partialModuleCount,
            ),
          },
        };
      })
    ),
  },

  mutations: {
    ...base.mutations,
  },

  actions: {
    ...base.actions,
    async searchGroups(
      ctx: IAction<IStateStrict<Partial<IGroups>>>,
      payload: IDataTableEmitPayload<IDataTableRouteQuery, ISearchCommandOptions>& { clientId: number, siteIds: number[] },
    ) {
      const elevationOrderBy: IOrderCommand[] = payload.filters.orderBy?.length
            && [ 'groupAccountModuleCount', 'groupPropertyModuleCount', 'groupSiteModuleCount' ]
              .includes(payload.filters.orderBy[0].field)
        ? [ { field: 'g.elevation', direction: payload.filters.orderBy[0].direction as IOrderCommand['direction'] } ]
        : [];

      await base.actions.search(ctx, mergeQueryCommandOptionsV2([
        defaultFilters(payload.clientId, payload.siteIds),
        {
          alias: defaultFilters(payload.clientId, payload.siteIds).alias,
          orderBy: elevationOrderBy,
          where: [
            Equal(`${defaultFilters(payload.clientId, payload.siteIds).alias}.clientId`, payload.clientId),
          ],
        },
        payload.filters as IQueryCommandOptions,
      ]) as ISearchCommandOptions);
    },
  },
};
